import { Search, X } from 'lucide-react';
import type { GroupFilters, GroupBuildSummary } from '../../types/customerDuplicates';
import { ALL_COMPANIES, COMPANY_LABELS } from '../../types/customerDuplicates';

interface Props {
    filters: GroupFilters;
    activeFilterCount: number;
    onChange: (next: Partial<GroupFilters>) => void;
    onClear: () => void;
    lastBuild: GroupBuildSummary | null;
    overdueDays: number;
}

/**
 * Filters for the group list, plus the honest line about the last build.
 *
 * The refused merges are shown, not hidden: a build that turned down a merge because a human said
 * NOT_DUPLICATE, or because a group would have exceeded its size or chain limit, has left accounts
 * out of a group on purpose. "Report, do not cap" (masterplan § 4 phase 4) means that number is on
 * screen, next to the groups it shaped.
 */
export function GroupsFiltersBar({
    filters, activeFilterCount, onChange, onClear, lastBuild, overdueDays,
}: Props) {
    const refused = lastBuild
        ? lastBuild.refusedNotDuplicate + lastBuild.refusedMaxSize + lastBuild.refusedMaxChain
        : 0;

    return (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                <input
                    type="text"
                    value={filters.search}
                    onChange={e => onChange({ search: e.target.value })}
                    placeholder="Customer name, code or ID…"
                    className="h-8 w-64 rounded border border-gray-300 pl-7 pr-2 text-sm focus:border-blue-500 focus:outline-none"
                />
            </div>

            <select
                value={filters.company}
                onChange={e => onChange({ company: e.target.value })}
                className="h-8 rounded border border-gray-300 px-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none"
            >
                <option value="">All companies</option>
                {ALL_COMPANIES.map(c => <option key={c} value={c}>{COMPANY_LABELS[c]}</option>)}
            </select>

            <select
                value={filters.warning}
                onChange={e => onChange({ warning: e.target.value })}
                className="h-8 rounded border border-gray-300 px-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none"
            >
                <option value="">Warning: any state</option>
                <option value="ANY">Companies disagree</option>
                <option value="BLOCKED">Blocked in one company only</option>
                <option value="OVERDUE">Overdue &gt; {overdueDays} days in one company only</option>
                <option value="NONE">No warning</option>
            </select>

            <label className="inline-flex items-center gap-1.5 text-sm text-gray-700">
                <input
                    type="checkbox"
                    checked={filters.multiCompany}
                    onChange={e => onChange({ multiCompany: e.target.checked })}
                    className="h-3.5 w-3.5 rounded border-gray-300"
                />
                Across companies only
            </label>

            {activeFilterCount > 0 && (
                <button
                    onClick={onClear}
                    className="inline-flex h-8 items-center gap-1 rounded border border-gray-300 px-2 text-sm text-gray-600 hover:bg-gray-50"
                >
                    <X className="h-3.5 w-3.5" /> Clear ({activeFilterCount})
                </button>
            )}

            <div className="ml-auto text-xs text-gray-500">
                {lastBuild ? (
                    <span title={
                        `Build #${lastBuild.runId}: ${lastBuild.edgesConsidered} candidate links considered, `
                        + `${lastBuild.edgesAccepted} accepted. Refused: ${lastBuild.refusedNotDuplicate} by a `
                        + `NOT_DUPLICATE decision, ${lastBuild.refusedMaxSize} by the group-size limit, `
                        + `${lastBuild.refusedMaxChain} by the chain-length limit. A refused merge leaves accounts `
                        + `out of a group on purpose — nothing is trimmed silently.`
                    }>
                        Last build #{lastBuild.runId}: {lastBuild.groupsBuilt.toLocaleString('de-CH')} groups
                        {refused > 0 && (
                            <> · <span className="text-amber-700">{refused.toLocaleString('de-CH')} merge{refused === 1 ? '' : 's'} refused</span></>
                        )}
                    </span>
                ) : (
                    <span className="text-amber-700">No group build has run yet.</span>
                )}
            </div>
        </div>
    );
}
